import breadcrumbComp from "../../common/breadcrumb-comp.vue";
import EncryptUtility from "../../utility/js/encrypt-utility";
import AddFavourite from "../../utility/js/addFav";
import salesService from "./sales-order-service.js";
import moment from "moment";

const STORAGE_KEY = "SODashboardFilters";
const currentAsOfDate = () => moment().format("YYYY-MM-DD");
const DEFAULT_FILTERS = {
  project: [],
  business: [],
  facility: [],
  trendDays: 7,
  asOfDate: currentAsOfDate(),
};
const TREND_CHART = {
  left: 78,
  right: 980,
  top: 16,
  bottom: 170,
};

export default {
  name: "salesOrderDashboard",
  components: {
    breadcrumbComp,
  },
  data() {
    return {
      userId: EncryptUtility.localStorageDecrypt("userID"),
      route: "",
      mainPage: "",
      subPage: "Sales Order Dashboard",
      pageUrl: "",
      backSlash: true,
      actionList: [],
      favouriteList: [],
      showAdd: false,
      asOfDateMenu: false,
      filters: { ...DEFAULT_FILTERS },
      projectList: [],
      businessList: [],
      facilityList: [],
      orderMatrixData: [],
      orderMatrixLoaded: false,
      dashboardInsight: null,
      hoveredTrendIndex: null,
      hoveredTrendSeries: null,
      hasPendingDashboardFilterChanges: false,
      suppressNextDashboardFilterChange: false,
      isApplyingDashboardFilters: false,
      dashboardPendingApplyTimer: null,
      trendDaysList: [
        { text: "7 Days", value: 7 },
        { text: "30 Days", value: 30 },
        { text: "60 Days", value: 60 },
        { text: "90 Days", value: 90 },
      ],
    };
  },
  computed: {
    asOfDateFormatted() {
      return this.filters.asOfDate ? moment(this.filters.asOfDate).format("MM/DD/YYYY") : "";
    },
    asOfDatePayload() {
      return moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate).format("YYYY-MM-DD");
    },
    ordersDroppedAsOfLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Orders Dropped Today";
      return `Orders Dropped ${asOfDate.format("MM/DD/YYYY")}`;
    },
    ordersDroppedPreviousLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Orders Dropped Yesterday";
      return `Orders Dropped ${asOfDate.subtract(1, "day").format("MM/DD/YYYY")}`;
    },
    ordersDroppedTrendSuffix() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      return asOfDate.isSame(moment(), "day") ? "yesterday" : "from the previous day";
    },
    ordersShippedLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Orders Shipped Today";
      return `Orders Shipped ${asOfDate.format("MM/DD/YYYY")}`;
    },
    dailyCancelsLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Cancellations Today";
      return `Cancellations ${asOfDate.format("MM/DD/YYYY")}`;
    },
    trendDaysPayload() {
      return parseInt(this.filters.trendDays || DEFAULT_FILTERS.trendDays);
    },
    facilityIdPayload() {
      const facilityIds = this.normalizeSelectedIds(this.filters.facility);
      return JSON.stringify(facilityIds.length ? facilityIds : [0]);
    },
    projectKeyPayload() {
      const projectIds = this.normalizeSelectedIds(this.filters.project);
      return JSON.stringify(projectIds.length ? projectIds : [0]);
    },
    businessKeyPayload() {
      const businessIds = this.normalizeSelectedIds(this.filters.business);
      return JSON.stringify(businessIds.length ? businessIds : [0]);
    },
    matrixSummary() {
      return this.firstDefined([
        this.orderMatrixData?.summary,
        this.orderMatrixData?.Summary,
        this.orderMatrixData?.dashboardSummary,
        this.orderMatrixData?.DashboardSummary,
      ]) || {};
    },
    metricCards() {
      return [
        {
          label: "Total Open Orders",
          value: this.metricValue(this.matrixSummary, ["totalOpenOrders", "TotalOpenOrders", "openOrders", "OpenOrders", "totalOpenOrderCount", "TotalOpenOrderCount"]),
          icon: "mdi-clipboard-list-outline",
          color: "#2563eb",
          trendIcon: this.directionIcon(this.totalOrderChangeDirection),
          trendLabel: this.totalOrderTrendLabel,
          trendClass: this.directionClass(this.totalOrderChangeDirection),
          trendSuffix: "vs yesterday",
        },
        {
          label: this.ordersDroppedAsOfLabel,
          value: this.metricValue(this.matrixSummary, ["ordersToday", "OrdersToday", "ordersDroppedToday", "OrdersDroppedToday", "droppedToday", "DroppedToday"]),
          icon: "mdi-cart-arrow-down",
          color: "#16a34a",
          trendIcon: this.directionIcon(this.changeDirection),
          trendLabel: this.directionLabel(this.changeDirection, this.orderDroppedPercentChange),
          trendClass: this.directionClass(this.changeDirection),
          trendSuffix: this.ordersDroppedTrendSuffix,
        },
        ...this.slaCategoryMetricCards,
        {
          label: this.ordersShippedLabel,
          value: this.metricValue(this.matrixSummary, ["dailyShipmentCount", "DailyShipmentCount"]),
          icon: "mdi-truck-check-outline",
          color: "#0f766e",
        },
        {
          label: this.dailyCancelsLabel,
          value: this.metricValue(this.matrixSummary, ["todayCancels", "TodayCancels"]),
          icon: "mdi-cancel",
          color: "#dc2626",
        },
      ];
    },
    orderStatusBreakdown() {
      return this.normalizeBreakdown(
        this.firstArray(this.orderMatrixData, ["orderStatusBreakdown", "OrderStatusBreakdown", "statusBreakdown", "StatusBreakdown", "openOrderStatus"], ["status", "Status", "count", "Count"]),
        ["orderStatus", "OrderStatus", "status", "Status", "label", "Label"],
        ["orderCount", "OrderCount", "count", "Count", "orders", "Orders", "value", "Value"],
        ["percentageOfTotal", "PercentageOfTotal"],
      );
    },
    slaSummary() {
      return this.normalizeBreakdown(
        this.firstArray(this.orderMatrixData, ["slaCategories", "SlaCategories", "SLACategories", "orderSlaSummary", "OrderSlaSummary", "slaSummary", "SlaSummary", "SLASummary"], ["sla", "SLA", "slaCount", "SlaCount", "count", "Count"]),
        ["sla", "SLA", "status", "Status", "label", "Label"],
        ["slaCount", "SlaCount", "SLACount", "count", "Count", "orders", "Orders", "value", "Value"],
        ["slaPercent", "SlaPercent", "SLAPercent"],
      ).map((item) => ({
        ...item,
        color: this.slaColor(item.label),
      }));
    },
    slaCategoryMetricCards() {
      return this.slaSummary.map((item) => ({
        label: item.label,
        value: item.value,
        icon: this.slaIcon(item.label),
        color: item.color,
      }));
    },
    trendRows() {
      return this.firstArray(this.orderMatrixData, ["orderDroppedTrend", "OrderDroppedTrend", "ordersDroppedTrend", "OrdersDroppedTrend", "trend", "Trend", "dailyTrend", "DailyTrend"], ["date", "Date", "orderDate"])
        .map((item) => ({
          date: this.firstDefined([item.orderDate, item.OrderDate, item.date, item.Date, item.snapshotDate, item.SnapshotDate]) || "",
          ordersDropped: this.normalizeNumber(this.firstDefined([item.ordersCreated, item.OrdersCreated, item.ordersDropped, item.OrdersDropped, item.droppedOrders, item.DroppedOrders, item.count, item.Count])),
          dailyAverage: this.normalizeNumber(this.firstDefined([item.rollingAverage, item.RollingAverage, item.dailyAverage, item.DailyAverage, item.average, item.Average])),
        }));
    },
    statusTotal() {
      return this.orderStatusBreakdown.reduce((total, item) => total + item.value, 0);
    },
    statusBreakdownMax() {
      return Math.max(...this.orderStatusBreakdown.map((item) => item.value), 1);
    },
    slaTotal() {
      return this.slaSummary.reduce((total, item) => total + item.value, 0);
    },
    trendPoints() {
      return this.buildTrendPoints(this.trendRows, "ordersDropped");
    },
    trendPolyline() {
      return this.trendPoints.map((point) => `${point.x},${point.y}`).join(" ");
    },
    trendBars() {
      if (!this.trendRows.length) return [];
      const max = this.trendScaleMax;
      const chartWidth = TREND_CHART.right - TREND_CHART.left;
      const chartHeight = TREND_CHART.bottom - TREND_CHART.top;
      const slotWidth = chartWidth / this.trendRows.length;
      const barWidth = Math.min(58, Math.max(16, slotWidth * 0.45));
      return this.trendRows.map((item, index) => {
        const centerX = TREND_CHART.left + slotWidth * index + slotWidth / 2;
        const height = (item.ordersDropped / max) * chartHeight;
        return {
          ...item,
          index,
          x: centerX - barWidth / 2,
          centerX,
          y: TREND_CHART.bottom - height,
          width: barWidth,
          height,
        };
      });
    },
    trendAveragePoints() {
      return this.buildTrendPoints(this.trendRows, "dailyAverage");
    },
    trendAveragePolyline() {
      return this.trendAveragePoints.map((point) => `${point.x},${point.y}`).join(" ");
    },
    trendGridLines() {
      const chartHeight = TREND_CHART.bottom - TREND_CHART.top;
      return this.trendYAxisTicks.map((tick) => ({
        value: tick,
        y: TREND_CHART.bottom - (tick / this.trendScaleMax) * chartHeight,
      }));
    },
    trendYAxisTicks() {
      const max = this.trendScaleMax;
      const step = max / 5;
      return Array.from({ length: 6 }, (_, index) => Math.round(step * index));
    },
    trendScaleMax() {
      const values = this.trendRows.flatMap((item) => [item.ordersDropped, item.dailyAverage]);
      const max = Math.max(...values, 1);
      return Math.ceil(max / 5) * 5;
    },
    changeDirection() {
      return String(this.firstDefined([this.matrixSummary.changeDirection, this.matrixSummary.ChangeDirection]) || "NO CHANGE").toUpperCase();
    },
    orderDroppedPercentChange() {
      return this.metricValue(this.matrixSummary, ["percentChange", "PercentChange"]);
    },
    totalOrderChangeDirection() {
      return String(this.firstDefined([
        this.matrixSummary.totalOrderChangeDirection,
        this.matrixSummary.TotalOrderChangeDirection,
      ]) || "NO CHANGE").toUpperCase();
    },
    totalOrderPercentChange() {
      return this.metricValue(this.matrixSummary, ["percentTotalOrderChange", "PercentTotalOrderChange"]);
    },
    totalOrderTrendLabel() {
      const directionLabel = this.totalOrderChangeDirection === "NEW" || this.totalOrderChangeDirection === "UP"
        ? "Up"
        : this.totalOrderChangeDirection === "NO CHANGE"
          ? "No Change"
          : this.toProperCase(this.totalOrderChangeDirection);
      return `${directionLabel} ${this.formatTotalOrderChangePercent(this.totalOrderPercentChange)}`;
    },
    changeDirectionIcon() {
      if (this.changeDirection === "NEW" || this.changeDirection === "UP") return "mdi-trending-up";
      if (this.changeDirection === "DOWN") return "mdi-trending-down";
      return "mdi-trending-neutral";
    },
    changeDirectionLabel() {
      if (this.changeDirection === "NEW") return "New";
      if (this.changeDirection === "UP") return "Up";
      if (this.changeDirection === "DOWN") return "Down";
      return "No Change";
    },
  },
  async created() {
    this.route = this.$route.path.replace("/", "");
    await this.loadBreadcrumbData();
    const hasPersistedFilters = this.restoreFilters();
    this.filters.asOfDate = currentAsOfDate();
    this.normalizeFilters();
    await this.loadFilterLists();
    if (hasPersistedFilters) await this.loadOrderMatrix();
  },
  methods: {
    async loadBreadcrumbData() {
      const data = await AddFavourite.getPageAction(this.userId, this.route);
      this.actionList = data?.actionList || [];
      this.mainPage = data?.mainPage || "Sales Order";
      this.favouriteList = data?.favouriteList || [];
      this.pageUrl = data?.pageUrl || this.route;
      this.subPage = data?.subPage || "Sales Order Dashboard";
      this.showAdd = data?.showAdd || false;
    },
    addFav() {
      this.showAdd = !this.showAdd;
      AddFavourite.add_remove_Favourite(this.showAdd, this.userId, this.route);
    },
    resetFunction() {
      this.refreshDashboard();
    },
    restoreFilters() {
      const savedFilters = EncryptUtility.localStorageDecrypt(STORAGE_KEY);
      if (!savedFilters) return false;
      try {
        const parsedFilters = JSON.parse(savedFilters);
        delete parsedFilters.asOfDate;
        EncryptUtility.localStorageEncrypt(STORAGE_KEY, JSON.stringify(parsedFilters));
        this.filters = {
          ...DEFAULT_FILTERS,
          ...parsedFilters,
          asOfDate: currentAsOfDate(),
        };
        if (!Array.isArray(this.filters.project)) this.filters.project = this.filters.project ? [this.filters.project] : [];
        if (!Array.isArray(this.filters.business)) this.filters.business = this.filters.business ? [this.filters.business] : [];
        if (!Array.isArray(this.filters.facility)) this.filters.facility = this.filters.facility ? [this.filters.facility] : [];
        if (Array.isArray(this.filters.trendDays)) this.filters.trendDays = this.filters.trendDays[0] || DEFAULT_FILTERS.trendDays;
        this.normalizeFilters();
        return true;
      } catch (err) {
        this.filters = { ...DEFAULT_FILTERS, asOfDate: currentAsOfDate() };
        return false;
      }
    },
    normalizeFilters() {
      if (!Array.isArray(this.filters.project)) this.filters.project = this.filters.project ? [this.filters.project] : [];
      if (!Array.isArray(this.filters.business)) this.filters.business = this.filters.business ? [this.filters.business] : [];
      if (!Array.isArray(this.filters.facility)) this.filters.facility = this.filters.facility ? [this.filters.facility] : [];
      this.filters.project = this.normalizeSelectedIds(this.filters.project);
      this.filters.business = this.normalizeSelectedIds(this.filters.business);
      this.filters.facility = this.normalizeSelectedIds(this.filters.facility);
      if (Array.isArray(this.filters.trendDays)) this.filters.trendDays = this.filters.trendDays[0] || DEFAULT_FILTERS.trendDays;
    },
    persistFilters() {
      const persistedFilters = { ...this.filters };
      delete persistedFilters.asOfDate;
      EncryptUtility.localStorageEncrypt(STORAGE_KEY, JSON.stringify(persistedFilters));
    },
    async refreshDashboard() {
      this.normalizeFilters();
      await this.loadFilterLists();
      await this.loadOrderMatrix();
    },
    async clearFilters() {
      this.filters = { ...DEFAULT_FILTERS, asOfDate: currentAsOfDate() };
      this.orderMatrixData = [];
      this.orderMatrixLoaded = false;
      this.dashboardInsight = null;
      this.hasPendingDashboardFilterChanges = false;
      EncryptUtility.localStorageRemoveItem(STORAGE_KEY);
      await this.loadFilterLists();
    },
    async onDashboardFilterSelectionChange(filterRef = "") {
      if (this.suppressNextDashboardFilterChange) return;
      this.normalizeFilters();
      this.hasPendingDashboardFilterChanges = true;
      await this.$nextTick();
      if (!this.isDashboardFilterDropdownOpen(filterRef)) {
        await this.applyDashboardFilters(filterRef);
      }
    },
    getDashboardFilterSelectionText(item) {
      return item?.text ?? item;
    },
    getDashboardFilterSelectionValue(item) {
      return item?.value ?? item;
    },
    async removeDashboardFilterSelection(filterName, item, filterRef = "") {
      const removedValue = this.getDashboardFilterSelectionValue(item);
      this.filters[filterName] = (this.filters[filterName] || []).filter(
        (value) => String(value) !== String(removedValue)
      );
      this.hasPendingDashboardFilterChanges = true;
      await this.applyDashboardFilters(filterRef);
    },
    async clearDashboardFilterSelection(filterName, filterRef = "") {
      this.suppressNextDashboardFilterChange = true;
      this.filters[filterName] = [];
      this.hasPendingDashboardFilterChanges = true;
      try {
        await this.$nextTick();
        await this.applyDashboardFilters(filterRef);
        await this.$nextTick();
      } finally {
        this.suppressNextDashboardFilterChange = false;
      }
    },
    applyPendingDashboardFilters(filterRef = "") {
      if (!this.hasPendingDashboardFilterChanges || this.isApplyingDashboardFilters) return;
      if (this.dashboardPendingApplyTimer) {
        clearTimeout(this.dashboardPendingApplyTimer);
      }
      this.dashboardPendingApplyTimer = setTimeout(async () => {
        this.dashboardPendingApplyTimer = null;
        if (this.hasPendingDashboardFilterChanges && !this.isApplyingDashboardFilters) {
          await this.applyDashboardFilters(filterRef);
        }
      }, 150);
    },
    async applyDashboardFilters(filterRef = "") {
      if (this.isApplyingDashboardFilters) return;
      if (this.dashboardPendingApplyTimer) {
        clearTimeout(this.dashboardPendingApplyTimer);
        this.dashboardPendingApplyTimer = null;
      }
      this.isApplyingDashboardFilters = true;
      try {
        this.normalizeFilters();
        this.persistFilters();
        await this.loadFilterLists();
        await this.loadOrderMatrix();
        this.hasPendingDashboardFilterChanges = false;
        this.closeDashboardFilterDropdown(filterRef);
      } finally {
        this.isApplyingDashboardFilters = false;
      }
    },
    closeDashboardFilterDropdown(filterRef) {
      if (!filterRef || !this.$refs?.[filterRef]) return;
      this.$nextTick(() => {
        const filter = this.$refs[filterRef];
        if (filter?.blur) filter.blur();
        if (filter?.isMenuActive !== undefined) filter.isMenuActive = false;
      });
    },
    isDashboardFilterDropdownOpen(filterRef) {
      if (!filterRef || !this.$refs?.[filterRef]) return false;
      return Boolean(this.$refs[filterRef]?.isMenuActive);
    },
    async onTrendDaysChange() {
      this.normalizeFilters();
      this.persistFilters();
      await this.loadOrderMatrix();
    },
    async onAsOfDateChange() {
      this.asOfDateMenu = false;
      this.normalizeFilters();
      this.persistFilters();
      await this.loadFilterLists();
      await this.loadOrderMatrix();
    },
    async loadFilterLists() {
      const filterData = await salesService.getSalesOrderDashboardFilterList(
        "get",
        parseInt(this.userId),
        this.projectKeyPayload,
        this.businessKeyPayload,
        this.facilityIdPayload,
      );
      if (filterData?.message === "NA") return;

      this.projectList = this.toSelectItems(this.firstArray(filterData, ["projects", "Projects", "projectList", "ProjectList", "ProjectInfo", "Project", "project_list"], ["projectID", "projectId", "ProjKey", "proj_key", "proj_id", "ProjectId", "project_id"]));
      this.businessList = this.toSelectItems(
        this.firstArray(filterData, ["businessUnits", "BusinessUnits", "BusinessInfo", "Businesses", "BusinessList", "ProjectSpecificBusinessInfo", "BUInfo", "BuInfo", "Business", "business_list", "businesses", "bu_list"], ["businessUnitID", "businessUnit", "Bu_id", "BuKey", "BuId", "bu_id", "bu_key", "business_id", "BusinessName", "business"]),
      );
      this.facilityList = this.toSelectItems(this.firstArray(filterData, ["facilities", "Facilities", "facilityList", "FacilityList", "FacilityInfo", "Facility", "facility_list"], ["facilityID", "facilityId", "FacilityID", "FacilityKey", "FacilityId", "facilityid", "facility_key", "facility_id", "LocaKey", "loca_key"]));
    },
    async loadOrderMatrix() {
      const matrixData = await salesService.getSalesOrderDashboardOrderMatrix(
        "get",
        parseInt(this.userId),
        this.trendDaysPayload,
        this.asOfDatePayload,
        this.facilityIdPayload,
        this.projectKeyPayload,
        this.businessKeyPayload,
      );
      this.orderMatrixLoaded = true;
      this.orderMatrixData = matrixData?.message === "NA" ? [] : matrixData;
      this.dashboardInsight = null;
    },
    metricValue(source, aliases) {
      return this.normalizeNumber(this.firstDefined(aliases.map((key) => source?.[key])));
    },
    directionIcon(direction) {
      if (direction === "NEW" || direction === "UP") return "mdi-trending-up";
      if (direction === "DOWN") return "mdi-trending-down";
      return "mdi-trending-neutral";
    },
    directionLabel(direction, percentChange = 0) {
      const percentLabel = this.formatChangePercent(percentChange);
      if (direction === "NEW" || direction === "UP") return `Up ${percentLabel}`;
      if (direction === "DOWN") return `Down ${percentLabel}`;
      return `No Change ${percentLabel}`;
    },
    directionClass(direction) {
      if (direction === "NEW") return "up";
      return String(direction || "NO CHANGE").toLowerCase().replace(" ", "-");
    },
    formatChangePercent(value) {
      const percentValue = Math.abs(this.normalizeNumber(value));
      if (percentValue > 0 && percentValue < 0.1) return `${percentValue.toFixed(2)}%`;
      return `${percentValue.toFixed(1)}%`;
    },
    formatTotalOrderChangePercent(value) {
      const percentValue = Math.abs(this.normalizeNumber(value));
      if (percentValue > 0 && percentValue < 0.1) return `${percentValue.toFixed(2)}%`;
      return `${percentValue.toFixed(1)}%`;
    },
    toProperCase(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    normalizeNumber(value) {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    normalizeBreakdown(items, labelAliases, valueAliases, percentAliases = []) {
      return items.map((item) => ({
        label: this.firstDefined(labelAliases.map((key) => item?.[key])) || "",
        value: this.normalizeNumber(this.firstDefined(valueAliases.map((key) => item?.[key]))),
        percent: this.normalizeNumber(this.firstDefined(percentAliases.map((key) => item?.[key]))),
      })).filter((item) => item.label);
    },
    formatNumber(value) {
      return this.normalizeNumber(value).toLocaleString();
    },
    formatPercent(value, total) {
      if (!total) return "0.0%";
      return `${((this.normalizeNumber(value) / total) * 100).toFixed(1)}%`;
    },
    formatBreakdownPercent(item, total) {
      return item.percent ? `${item.percent.toFixed(1)}%` : this.formatPercent(item.value, total);
    },
    formatDashboardDate(value) {
      return value ? moment(value).format("MMM D") : "";
    },
    colorForIndex(index) {
      const colors = ["#2563eb", "#16a34a", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#64748b", "#ec4899", "#14b8a6", "#a855f7"];
      return colors[index % colors.length];
    },
    slaColor(label) {
      const normalizedLabel = String(label || "").toUpperCase();
      if (normalizedLabel.includes("ON-TIME") || normalizedLabel.includes("ON TIME")) return "#2e7d32";
      if (normalizedLabel.includes("MUST SHIP")) return "#f2b824";
      if (normalizedLabel.includes("PAST DUE")) return "#d84315";
      return "#64748b";
    },
    slaIcon(label) {
      const normalizedLabel = String(label || "").toUpperCase();
      if (normalizedLabel.includes("ON-TIME") || normalizedLabel.includes("ON TIME")) return "mdi-check-circle-outline";
      if (normalizedLabel.includes("MUST SHIP")) return "mdi-alert-circle-outline";
      if (normalizedLabel.includes("PAST DUE")) return "mdi-clock-alert-outline";
      return "mdi-chart-donut";
    },
    normalizeStatusLabel(label) {
      return String(label || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
    },
    statusBarColor(label, index) {
      const normalizedLabel = this.normalizeStatusLabel(label);
      if (normalizedLabel.includes("BACKORDER")) return "#dc2626";
      if (normalizedLabel.includes("READY TO PICK")) return "#16a34a";
      if (normalizedLabel.includes("HOLD") || normalizedLabel.includes("HELD")) return "#f59e0b";

      const colors = ["#4f8f3a", "#f2c23e", "#d95f38", "#7c4dd8", "#5db8ad", "#e5b744", "#6bb7b5", "#d95a83", "#8b949e"];
      return colors[index % colors.length];
    },
    statusBarWidth(item) {
      return Math.max((this.normalizeNumber(item.value) / this.statusBreakdownMax) * 100, item.value ? 3 : 0);
    },
    selectBreakdownItem(title, item, total, index) {
      this.dashboardInsight = {
        type: "breakdown",
        title,
        label: item.label,
        value: this.formatNumber(item.value),
        detail: this.formatBreakdownPercent(item, total),
        color: this.statusBarColor(item.label, index),
      };
    },
    selectTrendPoint(point, series = "orders") {
      this.hoveredTrendIndex = point.index;
      this.hoveredTrendSeries = series;
      const isAverage = series === "average";
      this.dashboardInsight = {
        type: "trend",
        series,
        title: isAverage ? "Rolling Average" : "Orders Dropped",
        label: this.formatDashboardDate(point.date),
        value: this.formatNumber(isAverage ? point.dailyAverage : point.ordersDropped),
        detail: isAverage ? "Trend line" : "Bar value",
        color: isAverage ? "#3f5cff" : "#ff5a52",
      };
    },
    clearTrendHover() {
      this.hoveredTrendIndex = null;
      this.hoveredTrendSeries = null;
    },
    clearDashboardInsight() {
      this.dashboardInsight = null;
    },
    donutSegments(items, total) {
      if (!total) return [];
      let cursor = 0;
      return items.map((item, index) => {
        const portion = item.value / total;
        const start = cursor;
        cursor += portion;
        return {
          item,
          index,
          color: item.color || this.colorForIndex(index),
          path: this.donutArcPath(90, 90, 70, start * 359.99, cursor * 359.99),
        };
      });
    },
    donutArcPath(cx, cy, radius, startAngle, endAngle) {
      const start = this.polarToCartesian(cx, cy, radius, endAngle);
      const end = this.polarToCartesian(cx, cy, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
    },
    polarToCartesian(cx, cy, radius, angleInDegrees) {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
      return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians),
      };
    },
    donutStyle(items) {
      const colors = ["#2563eb", "#16a34a", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#64748b"];
      const total = items.reduce((sum, item) => sum + item.value, 0);
      if (!total) return { background: "#e5e7eb" };
      let cursor = 0;
      const stops = items.map((item, index) => {
        const start = cursor;
        cursor += (item.value / total) * 100;
        return `${colors[index % colors.length]} ${start}% ${cursor}%`;
      });
      return { background: `conic-gradient(${stops.join(", ")})` };
    },
    buildTrendPoints(rows, valueKey) {
      if (!rows.length) return [];
      const max = this.trendScaleMax;
      const chartWidth = TREND_CHART.right - TREND_CHART.left;
      const chartHeight = TREND_CHART.bottom - TREND_CHART.top;
      const slotWidth = chartWidth / rows.length;
      return rows.map((row, index) => ({
        x: TREND_CHART.left + slotWidth * index + slotWidth / 2,
        y: TREND_CHART.bottom - (row[valueKey] / max) * chartHeight,
        value: row[valueKey],
        date: row.date,
        ordersDropped: row.ordersDropped,
        dailyAverage: row.dailyAverage,
        index,
      }));
    },
    firstArray(source, keys, itemKeys = [], allowDirectArray = true) {
      if (Array.isArray(source)) {
        if (allowDirectArray || this.arrayMatchesItemKeys(source, itemKeys)) return source;
        return [];
      }
      for (const key of keys) {
        if (Array.isArray(source?.[key])) return source[key];
      }
      if (source && typeof source === "object") {
        for (const [key, value] of Object.entries(source)) {
          if (keys.includes(key) && Array.isArray(value)) return value;
          if (Array.isArray(value) && this.arrayMatchesItemKeys(value, itemKeys)) return value;
          const nestedArray = this.firstArray(value, keys, itemKeys, false);
          if (nestedArray.length) return nestedArray;
        }
      }
      return [];
    },
    arrayMatchesItemKeys(items, itemKeys) {
      return itemKeys.length > 0 && items.some((item) => (
        item &&
        typeof item === "object" &&
        itemKeys.some((key) => item[key] !== undefined && item[key] !== null)
      ));
    },
    toSelectItems(items) {
      return items.map((item) => {
        if (typeof item !== "object" || item === null) {
          return { text: item, value: this.normalizeId(item) };
        }
        const text =
          this.firstDefined([
            item.Project,
            item.ProjectName,
            item.projectName,
            item.project,
            item.project_name,
            item.Business,
            item.BusinessName,
            item.businessUnit,
            item.BUName,
            item.business,
            item.business_name,
            item.bu_name,
            item.BU,
            item.bu,
            item.Facility,
            item.FacilityName,
            item.facilityName,
            item.facility,
            item.facility_name,
            item.Name,
            item.name,
            item.Text,
            item.text,
            item.Description,
            item.description,
            item.Value,
            item.value,
          ]);
        const value =
          this.firstDefined([
            item.ProjKey,
            item.Proj_Key,
            item.projectID,
            item.projectId,
            item.proj_key,
            item.proj_id,
            item.ProjectId,
            item.project_id,
            item.ProjectKey,
            item.project_key,
            item.BuKey,
            item.BuId,
            item.businessUnitID,
            item.bu_key,
            item.bu_id,
            item.business_id,
            item.Bu_id,
            item.FacilityKey,
            item.FacilityID,
            item.FacilityId,
            item.facilityID,
            item.facilityId,
            item.facilityid,
            item.facility_key,
            item.facility_id,
            item.LocaKey,
            item.loca_key,
            item.Value,
            item.value,
            text,
          ]);
        return { text, value: this.normalizeId(value) };
      }).filter((item) => item.text !== undefined && item.value !== undefined);
    },
    firstDefined(values) {
      return values.find((value) => value !== undefined && value !== null);
    },
    normalizeId(value) {
      const numericValue = Number(value);
      return Number.isInteger(numericValue) ? numericValue : value;
    },
    normalizeSelectedIds(values) {
      return (values || []).map((value) => this.normalizeId(value)).filter((value) => Number.isInteger(value));
    },
  },
};
